import { action, observable, runInAction } from 'mobx';
import * as IoC from 'typescript-ioc';
import { AbstractFormViewModelController } from '../../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { DefaultPageConfigurationType, DefaultPageType, GENERAL_SESSIONS, MY_SCHEDULE } from '../../../../../../../api/custom-pages.dao';
import { DefaultPageEntity, EntityProps } from '../../../../../../../modules/passport-pages/default-pages/default-page.entity';
import { FormVmItemConfigType, FormVmValueType } from '../../../../../../../infrastructure/form-viewmodel-controller';
import { CustomPageFormField } from '../../../../../../../modules/passport-pages/custom-pages';
import { DefaultPageEntityConfig } from '../../../../../../../modules/passport-pages/default-pages/default-page-entity.config';
import { RESTRICT_ACCESS_FILTERS, RESTRICT_ACCESS_ENABLED, WELCOME_KNOVIO_EMBED_ID, WELCOME_KNOVIO_ENABLED, defaultPagesConfig, SUBSCRIBE_ATTENDEE_TO_MEETING_ON_REGISTRATION_ENABLED } from '../../../../../../../modules/passport-pages/config';
import { IPassportSettingsConfigurationDAO, IPassportSettingsConfigurationDTO } from '../../../../../../../api/passport-settings-configuration.dao';
import { IGlobalStorage } from '../../../../../../../infrastructure/global-storage';
import { handleApiException } from '../../../../../../global/modal/alerts-host/alert-templates';


export class DefaultPageViewModel extends AbstractFormViewModelController {
    @observable public passportSettingsConfigurationDAO: IPassportSettingsConfigurationDAO = IoC.Container.get(IPassportSettingsConfigurationDAO);
    @observable public passportSettings: IPassportSettingsConfigurationDTO;
    @observable public isLoading = false;
    @observable private globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage);

    public static create(entity: DefaultPageEntity): DefaultPageViewModel {
        const config = DefaultPageEntityConfig.getVmConfigByType(entity.getType());
        return new DefaultPageViewModel(entity, config);
    }

    public getType(): DefaultPageType {
        return this.entity.getType();
    }
    public isEnabled(): boolean {
        return this.entity.isEnabled();
    }
    public isPublicEnabled(): boolean {
        return this.entity.isPublicEnabled();
    }
    public get isToggleDisabled(): boolean {
        return !this.entity.canBeDisabled();
    }

    public get pageName(): string {
        return this.isGeneralSessionsPage
            ? GENERAL_SESSIONS
            : this.isAgendaPage
            ? MY_SCHEDULE
            : this.getType();
    }
    @observable
    private isAccessFilters: boolean = true;

    public get accessFilters(): boolean {
        return this.isAccessFilters;
    }

    @action.bound
    public setAccessFiltersValid(status: boolean){
        this.isAccessFilters = status;
    }

    @action.bound
    private checkAccessFilters(): boolean {
        const valid = this.getFieldByKey(RESTRICT_ACCESS_ENABLED).value === true &&
            !this.getFieldByKey(RESTRICT_ACCESS_FILTERS).value.toString().length;
        this.setAccessFiltersValid(!valid);
        return valid;
    }

    @action.bound
    public togglePage(): void {
        if (this.entity.isEnabled()) {
            this.entity.disable();
        } else {
            this.entity.enable();
        }
    }

    @action.bound
    public togglePublicPage(): void {
        if (this.entity.isPublicEnabled()) {
            this.entity.publicDisable();
        } else {
            this.entity.publicEnable();
        }
    }

    public isPageLinkEnabled(): boolean {
        return this.entity.isPageLinkEnabled();
    }

    @action.bound
    public togglePageLinkEnabled(): void {
        this.entity.togglePageLinkEnabled();
    }

    @action.bound
    public setFieldValueByKey(key: string, value: FormVmValueType): void {
        this.setValue(key, value);
    }

    @action.bound
    public setFieldDefaultValueByKey(key: string): void {
        this.setValue(key, defaultPagesConfig[key].defaultValue);
    }

    @action
    public async fetchSettings() {
        this.isLoading = true;

        try {
            const event = this.globalStorage.getEvent();
            const accountId = event?.accountId;
            const eventId = event?.eventId;

            if (!accountId || !eventId) {
                return;
            }

            const passportSettings = await this.passportSettingsConfigurationDAO.getPassportSettingsConfiguration({
                accountId: accountId,
                eventId: eventId
            });

            runInAction(() => {
                this.passportSettings = passportSettings;

            });

        } catch (error) {
            runInAction(() => {
                handleApiException(error);
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    public isValid(): boolean {
        if (this.isWelcomePageType) return this.isWelcomePageValid();
        if (this.isGeneralSessionsPage) return this.isSessionPageValid();
        if (this.isAgendaPage) return this.isAgendaPageValid();
        if (this.isReplaysPage) return this.isReplaysPageValid();
        return this.isVmValid();
    }

    public getFieldByKey(key: string): CustomPageFormField {
        return {
            value: this.getValue(key),
            ...this.getValueValidationState(key)
        };
    }

    public getVmProps(): DefaultPageConfigurationType {
        return this.getVmState()
            .reduce((props, { name, value }) => {
                props[name] = value;
                return props;
            }, {} as DefaultPageConfigurationType);
    }

    public getEntityProps(): EntityProps {
        return this.entity.getProps();
    }

    public getFieldConfigByKey(key: string): FormVmItemConfigType {
        return this.config.find(cnf => cnf.name === key);
    }

    private constructor(private readonly entity: DefaultPageEntity, private readonly config: FormVmItemConfigType[]) {
        super(entity.getProps(), entity.getType(), config);
    }

    private get isReplaysPage(): boolean {
        return this.getType() === DefaultPageType.Replays;
    }

    private isWelcomePageValid(): boolean {
        return this.isWelcomePageType && this.getFieldByKey(WELCOME_KNOVIO_ENABLED).value
            ? this.isVmValid() && !!this.getFieldByKey(WELCOME_KNOVIO_EMBED_ID).value
            : this.isVmValid()
        ;
    }

    private validatePageWithAccessFilters(): boolean {
        return this.checkAccessFilters() ? false : this.isVmValid();
    }

    private isSessionPageValid(): boolean {
        return this.validatePageWithAccessFilters();
    }

    private isAgendaPageValid(): boolean {
        return this.validatePageWithAccessFilters();
    }

    private isReplaysPageValid(): boolean {
        return this.validatePageWithAccessFilters();
    }

    private get isShowcasePageType(): boolean {
        return this.getType() === DefaultPageType.Showcase;
    }
    private get isWelcomePageType(): boolean {
        return this.getType() === DefaultPageType.Welcome;
    }
    private get isSupportPageType(): boolean {
        return this.getType() === DefaultPageType.Support;
    }
    private get isGeneralSessionsPage(): boolean {
        return this.getType() === DefaultPageType.Session;
    }
    private get isAgendaPage(): boolean {
        return this.getType() === DefaultPageType.Agenda;
    }
    @action.bound
    public toggleSubscribeOnRegistration(): void {
        const currentValue = this.getFieldByKey(SUBSCRIBE_ATTENDEE_TO_MEETING_ON_REGISTRATION_ENABLED).value as boolean;
        this.setFieldValueByKey(SUBSCRIBE_ATTENDEE_TO_MEETING_ON_REGISTRATION_ENABLED, !currentValue);
    }

}

